import { Autocomplete, Box, Stack, Typography } from '@locus-taxy/encore-mui';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { getTestId, buildFormTestId } from '@locus-taxy/encore-utils';
import { TestIdAction } from '../../utils/testIdUtils';

interface LocaleSettingsSectionProps {
  timezone: string;
  timezoneOptions: string[];
  locale: string;
  localeOptions: string[];
  onTimezoneChange: (timezone: string) => Promise<void>;
  onLocaleChange: (locale: string) => Promise<void>;
  isUpdating: boolean;
  canUpdate: boolean;
}

export function LocaleSettingsSection({
  timezone,
  timezoneOptions,
  locale,
  localeOptions,
  onTimezoneChange,
  onLocaleChange,
  isUpdating,
  canUpdate,
}: LocaleSettingsSectionProps) {
  const { t } = useTranslation();
  const handleTimezoneChange = async (
    _event: unknown,
    newValue: string | string[] | null
  ) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    if (value && value !== timezone) {
      await onTimezoneChange(value);
    }
  };

  const handleLocaleChange = async (
    _event: unknown,
    newValue: string | string[] | null
  ) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    if (value && value !== locale) {
      await onLocaleChange(value);
    }
  };

  return (
    <Box
      sx={{
        borderRadius: 1,
        mb: 4,
        backgroundColor: 'white',
      }}
      data-testid={getTestId({
        action: TestIdAction.VIEW,
        object: 'locale-settings-section',
        page: 'account',
      })}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('userDetails.localeSettings')}
        </Typography>
        <Stack spacing={2} maxWidth={600}>
          <Autocomplete
            options={timezoneOptions.length > 0 ? timezoneOptions : [timezone]}
            value={timezone}
            clearable={false}
            label={t('userDetails.timezone')}
            getOptionLabel={option => option}
            isOptionEqualToValue={(opt, val) => opt === val}
            onChange={handleTimezoneChange}
            disabled={isUpdating || !canUpdate}
            data-testid={buildFormTestId({
              fieldType: 'select',
              fieldName: 'timezone',
              entity: 'account',
              formAction: 'update',
            })}
          />
          <Autocomplete
            options={localeOptions.length > 0 ? localeOptions : [locale]}
            value={locale}
            clearable={false}
            label={t('userDetails.language')}
            getOptionLabel={option => option}
            isOptionEqualToValue={(opt, val) => opt === val}
            onChange={handleLocaleChange}
            disabled={isUpdating || !canUpdate}
            data-testid={buildFormTestId({
              fieldType: 'select',
              fieldName: 'locale',
              entity: 'account',
              formAction: 'update',
            })}
          />
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}
        >
          {t('userDetails.localeSettingsDescription')}
        </Typography>
      </Box>
    </Box>
  );
}
